/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, forkJoin, from, merge, Observable, of, Subject} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  pairwise,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {TimeAndAttendanceComponent} from '../../+payrolls/prepare/time/time-and-attendance.component';
import {EmployeeVariablesService} from '../../+payrolls/prepare/variable-elements/employee-variables.service';
import {VariableElementsComponent} from '../../+payrolls/prepare/variable-elements/variable-elements.component';
import {DATE_MAX, DATE_MIN} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {EmpContractAttributes, FeatureEnum} from '../../models/models.interfaces';
import {CliCorePropertiesService} from '../../navigation/cli-core-properties.service';
import {Data} from '../api';
import {RolesService} from '../authentication/roles/roles.service';
import {ChangeTrackingComponent} from '../change-tracking/change-tracking.component';
import {ContractAlertsComponent} from '../contract-alerts/contract-alerts.component';
import {ContractAlertsService} from '../contract-alerts/contract-alerts.service';
import {CookieService} from '../cookie-service/cookie.service';
import {AttachmentsComponent} from '../employees/attachments.component';
import {EmployeePayslipsComponent} from '../employees/employee-payslips.component';
import {EmployeesUtilsService} from '../employees/employees-utils.service';
import {Contract, Employee} from '../employees/employees.model';
import {EmployeesService} from '../employees/employees.service';
import {FileChangesSessionService} from '../files/file-changes-session.service';
import {FileChangesService} from '../files/file-changes.service';
import {DataTransformer} from '../form-generator/data-transformer/data-transformer';
import {FormDescriptorService} from '../form-generator/form-descriptor.service';
import {
  BlockingError,
  FormDescriptor,
  FormValues,
  GroupZoneDescriptor,
  SimulationContractData,
  SimulationContractZoneValue,
  SimulationResponseByTab,
  TabSheetDescriptor,
  ZoneDescriptor,
  ZoneValuesByGroup,
  ZoneValues
} from '../form-generator/form-generator.interfaces';
import {FormGeneratorService} from '../form-generator/form-generator.service';
import {FormGeneratorUtils} from '../form-generator/form-generator.utils';
import {GeneratedFormComponent} from '../form-generator/generated-form/generated-form.component';
import {HrCalendarWorkScheduleUtil} from '../hr-calendar/hr-calendar-utils/hr-calendar-work-schedule.util';
import {HrCalendarContractPeriod, HrCalendarWorkSchedule} from '../hr-calendar/hr-calendar.models';
import {ImageManagementComponent} from '../image-management/image-management.component';
import {LogEventComponent} from '../log-event/log-event.component';
import {ItemType} from '../log-event/log-event.model';
import {LogEventService} from '../log-event/log-event.service';
import {ModalService} from '../modal';
import {DateLocalizationPipe} from '../pipes/date-localization.pipe';
import {
  PopulationCriteria,
  POPULATION_CRITERIA_COOKIE_NAME
} from '../population/create-population/create-population.interfaces';
import {PayrollInputAdminComponent} from '../sdconnect/payroll-input-admin/payroll-input-admin.component';
import {PersonMasterDataAdminComponent} from '../sdconnect/person-master-data-admin/person-master-data-admin.component';
import {SdcPayrollFormComponent} from '../sdconnect/sdc-payroll-form/sdc-payroll-form.component';
import {SdcPersonFormComponent} from '../sdconnect/sdc-person-form/sdc-person-form.component';
import {SdcStatusDTO} from '../sdconnect/sdconnect.model';
import {SdconnectService} from '../sdconnect/sdconnect.service';
import {GuidedTourService, GuidedTourStep} from '../ui/guided-tour/guided-tour.service';
import {User, UserService} from '../user';
import {strToDate} from '../utils/date';
import {isPresent, isValueConsideredAsEmpty} from '../utils/lang';
import {getInitials} from '../utils/strings';
import {ClipboardService} from 'ngx-clipboard';

// In-memory storage of the initial tab to open the descriptor on. Used to open another employee file on the same
// tab open for the currently browsed employee.
let initialTab = null;

type FormValueNode = Record<string, unknown>;

interface SimulationDiff {
  path: string;
  zoneId: string;
  before: unknown;
  after: unknown;
}

export const createTabSheetDescriptor = (tabSheetID: string, tabSheetPosition: number, transId: string,
                                         groupZones = [], translate = '', error = false): TabSheetDescriptor =>
  ({tabSheetID, tabSheetPosition, transId, groupZones, translate, error});

const IMAGE_MANAGEMENT_MODAL_ID = 'ImageManagementComponent';

/**
 * Employee detail : displays the employee form for a contract of the employee, letting the employee select the contract
 * to edit.
 */
@Component({
  selector: 'gp-employee-form',
  providers: [FileChangesSessionService],
  templateUrl: './employee-form.component.html',
  styleUrls: ['./employee-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployeeFormComponent extends Destroyable implements OnInit, OnChanges, OnDestroy {

  public readonly INLINE_PANEL = {
    DOCUMENTS: 'documents',
    ATTACHMENTS: 'attachments',
    CHANGE_TRACKING: 'changeTracking',
    EVENTS_LOG: 'eventsLog',
    ALERTS: 'contractAlerts',
    PERSON_MASTER_DATA_ADMIN: 'personMasterDataAdmin',
    PAYROLL_INPUT_ADMIN: 'payrollInputAdmin',
    SDC_PERSON: 'sdcPerson',
    SDC_PAYROLL: 'sdcPayroll',
    PAYSLIPS: 'payslips',
    TIME: 'time',
    HR_CALENDAR: 'hrCalendar'
  } as const;

  public readonly INLINE_PANEL_TITLE_MAP: { [key: string]: string } = {
    [this.INLINE_PANEL.DOCUMENTS]: 'DOCUMENTS.employee_documents',
    [this.INLINE_PANEL.ATTACHMENTS]: 'EMPLOYEE_MANAGE.attachments',
    [this.INLINE_PANEL.CHANGE_TRACKING]: 'CHANGE_TRACKING.change_tracking',
    [this.INLINE_PANEL.EVENTS_LOG]: 'EVENT_LOG.events_log',
    [this.INLINE_PANEL.ALERTS]: 'ALERTS.contract_alerts',
    [this.INLINE_PANEL.PERSON_MASTER_DATA_ADMIN]: 'Person Master Data Admin',
    [this.INLINE_PANEL.PAYROLL_INPUT_ADMIN]: 'Payroll Input Admin',
    [this.INLINE_PANEL.SDC_PERSON]: 'SDC.sdc_person_form',
    [this.INLINE_PANEL.SDC_PAYROLL]: 'SDC.sdc_payroll_form',
    [this.INLINE_PANEL.PAYSLIPS]: 'EMPLOYEE_MANAGE.payslips',
    [this.INLINE_PANEL.TIME]: 'PAYROLL_PREPARE.time_attendance',
    [this.INLINE_PANEL.HR_CALENDAR]: 'HR_CALENDAR.hr_calendar'
  };

  /**
   * Maps legacy/deep-link tab ids (e.g. coming from the employee list preview widget query param
   * `?tab=ContractAlerts`) to the inline panel that replaced the former tab. These ids are no
   * longer real form tabs, so they must open the matching inline panel instead of being passed
   * to generatedForm.redirectToTab() (which would otherwise fail to find the tab).
   */
  public readonly DEEPLINK_PANEL_MAP: { [tabId: string]: string } = {
    Time: this.INLINE_PANEL.TIME,
    ContractAlerts: this.INLINE_PANEL.ALERTS,
    EventsLog: this.INLINE_PANEL.EVENTS_LOG,
    Payslips: this.INLINE_PANEL.PAYSLIPS,
    PersonMasterDataAdmin: this.INLINE_PANEL.PERSON_MASTER_DATA_ADMIN,
    PayrollInputAdmin: this.INLINE_PANEL.PAYROLL_INPUT_ADMIN,
    SdcPersonForm: this.INLINE_PANEL.SDC_PERSON,
    SdcPayrollForm: this.INLINE_PANEL.SDC_PAYROLL,
    ChangeTracking: this.INLINE_PANEL.CHANGE_TRACKING
  };

  public user: User;

  @ViewChild(GeneratedFormComponent)
  public generatedForm: GeneratedFormComponent;

  @ViewChild(AttachmentsComponent)
  public attachmentsPanel: AttachmentsComponent;

  @ViewChild(ChangeTrackingComponent)
  public changeTrackingPanel: ChangeTrackingComponent;

  @ViewChild(LogEventComponent)
  public logEventPanel: LogEventComponent;

  @ViewChild(ContractAlertsComponent)
  public contractAlertsPanel: ContractAlertsComponent;

  @ViewChild(PersonMasterDataAdminComponent)
  public personMasterDataAdminPanel: PersonMasterDataAdminComponent;

  @ViewChild(PayrollInputAdminComponent)
  public payrollInputAdminPanel: PayrollInputAdminComponent;

  @ViewChild('sdcPersonPanel')
  public sdcPersonPanel: SdcPersonFormComponent;

  @ViewChild('sdcPayrollPanel')
  public sdcPayrollPanel: SdcPayrollFormComponent;

  @ViewChild(EmployeePayslipsComponent)
  public payslipsPanel: EmployeePayslipsComponent;

  @ViewChild('employeeDetailContent')
  public employeeDetailContentRef: ElementRef<HTMLElement>;

  public variableTab: VariableElementsComponent;

  @ViewChild('timePanel')
  public timeTab: TimeAndAttendanceComponent;

  @Input()
  public contractId: number;

  @Input()
  public employeeId: number;

  @Input()
  public readOnly = false;

  @Input()
  public sticky = false;

  @Input()
  public isPeopleRead = false;

  @Input()
  public employee: Employee;

  @Input()
  public isSkillModule = false;

  public formDescriptor: FormDescriptor;

  private zonesByTab: Map<string, string>;
  private descriptors: Map<number, FormDescriptor> = new Map<number, FormDescriptor>();
  private allCalculationDependencies: string[];

  public contracts: EmpContractAttributes[];
  public selectedContract: Data<EmpContractAttributes>;
  public selectedContractSdcStatus: SdcStatusDTO;
  public loadingForm: boolean;
  public values: FormValues;
  public hrCalendarWorkSchedules: HrCalendarWorkSchedule[] = [];

  @Input()
  public initialTab: string = initialTab;

  public loading = false;
  public saving = false;
  public isModalOpened = false;

  public today: Date = moment().startOf('day').toDate();
  public contractBeginDate: Date;

  public cliRegId: number;
  public peopleTreeEmitter: EventEmitter<any> = new EventEmitter<any>();

  public isVariableReadOnly = true;
  public isTimeReadOnly = true;

  public activeCardSubject: Subject<boolean> = new Subject();

  public blockingErrors: BlockingError[] = [];
  public populationCriteriaCookie: PopulationCriteria;

  private repInheritedDirtyDataByGroupZone: Map<string, boolean> = new Map();

  public isFormLoaded = false;
  public isCopied = false;

  public employeePosition: string = null;
  public employeeLocation: string = null;
  public employeeBusinessUnit: string = null;

  public isActionButtonDisabled = false;
  public isInlinePanelOpen = false;
  public activeInlinePanel: string = null;
  public inlinePanelHeightPx: string = null;
  public hasAttachmentPanel = false;
  public changeTrackingInitFn: (entityId: number, entityType: string, itemType?: string) => void;

  public sdConnectEnabled = false;
  public sdcPersonEnabled = false;
  public hrCalendarEnabled = false;
  public sdcPayrollEnabled = false;
  public hasContractAlerts = false;
  public hasEventErrors = false;

  /** Tracks which sidebar view is active: 'employee' for EMP_TAB/EMP_SKILL_TAB, 'contract' for CONTRACT_TAB */
  public activeSidebarView: 'employee' | 'contract' = null;

  /** Tab IDs that should be hidden based on the active sidebar view filter */
  public get viewFilteredHiddenTabIds(): string[] {
    if (!this.activeSidebarView || !this.formDescriptor) {
      return [];
    }
    const hiddenIds: string[] = [];
    this.formDescriptor.tabSheets.forEach(tab => {
      if (!this.tabBelongsToView(tab, this.activeSidebarView)) {
        hiddenIds.push(tab.tabSheetID);
      }
    });
    return hiddenIds;
  }

  /** Whether any employee-type tabs exist in the descriptor */
  public get hasEmployeeTabs(): boolean {
    return this.formDescriptor?.tabSheets?.some(t => t.tabType === 'EMP_TAB' || t.tabType === 'EMP_SKILL_TAB') || false;
  }

  /** Whether any contract-type tabs exist in the descriptor */
  public get hasContractTabs(): boolean {
    return this.formDescriptor?.tabSheets?.some(t => t.tabType === 'CONTRACT_TAB' || !t.tabType) || false;
  }

  /** Whether at least one employee-type tab currently has an error */
  public get hasEmployeeTabError(): boolean {
    return this.hasTabErrorForView('employee');
  }

  /** Whether at least one contract-type tab currently has an error */
  public get hasContractTabError(): boolean {
    return this.hasTabErrorForView('contract');
  }

  private hasTabErrorForView(view: 'employee' | 'contract'): boolean {
    if (!this.generatedForm || !this.generatedForm.errorMap || !this.formDescriptor) {
      return false;
    }
    return this.formDescriptor.tabSheets.some(tab => {
      if (!this.generatedForm.errorMap.get(tab.tabSheetID)) {
        return false;
      }
      return this.tabBelongsToView(tab, view);
    });
  }

  public get formIsPristine() {
    if (!isPresent(this.generatedForm) || !isPresent(this.generatedForm.form)) {
      return true;
    }
    return !this.generatedForm.loaded
      || (this.generatedForm.form.pristine
        && !this.isTimeTabDirty
        && !this.isVariableTabDirty);
  }

  public get isTimeTabDirty(): boolean {
    return this.timeTab && this.timeTab.isDirty();
  }

  public get isVariableTabDirty(): boolean {
    return this.variableTab && this.variableTab.isDirty();
  }

  private canRunSimulation(diff: SimulationDiff[]): boolean {
    if (!this.generatedForm?.form || !this.hasSimulationDependencies()) {
      return false;
    }

    const dependencies = new Set(
      (this.allCalculationDependencies ?? []).map(zoneId => FormGeneratorUtils.sanitizeZoneName(zoneId))
    );

    const hasAnyDependency = diff.some(item =>
      this.getDiffZoneIds(item).some(zoneId => dependencies.has(zoneId))
    );

    return (
      hasAnyDependency &&
      this.generatedForm.loaded &&
      !this.generatedForm.form.pristine &&
      !this.isTimeTabDirty &&
      !this.isVariableTabDirty &&
      !this.isActionButtonDisabled
    );
  }

  private hasSimulationDependencies(): boolean {
    return (this.allCalculationDependencies ?? [])
      .some(zoneId => typeof zoneId === 'string' && zoneId.trim().length > 0);
  }

  private getDiffZoneIds(item: SimulationDiff): string[] {
    const zoneIds = new Set<string>();
    const leafZoneId = item.zoneId;
    if (leafZoneId) {
      zoneIds.add(leafZoneId);
    }

    if (leafZoneId === 'value' || leafZoneId === 'transId') {
      const pathParts = item.path?.split('.') ?? [];
      if (pathParts.length >= 2) {
        zoneIds.add(pathParts[pathParts.length - 2]);
      }
    }

    return Array.from(zoneIds).map(zoneId => FormGeneratorUtils.sanitizeZoneName(zoneId));
  }

  private inheritedGroupZonesMap: Map<string, any> = new Map();
  private _isPeopleRead: boolean;
  private selectedContractDeleted = false;

  constructor(private employeesService: EmployeesService,
              private fileChangesService: FileChangesService,
              private fileChanges: FileChangesSessionService,
              private modalService: ModalService,
              private route: ActivatedRoute,
              private router: Router,
              private translate: TranslateService,
              private cd: ChangeDetectorRef,
              private cookieService: CookieService,
              private formGeneratorService: FormGeneratorService,
              private userService: UserService,
              private rolesService: RolesService,
              private dateLocalizationPipe: DateLocalizationPipe,
              private variableService: EmployeeVariablesService,
              private employeesUtilsService: EmployeesUtilsService,
              private logEventService: LogEventService,
              private formDescriptorService: FormDescriptorService,
              private sdconnectService: SdconnectService,
              private contractAlertsService: ContractAlertsService,
              private guidedTourService: GuidedTourService,
              private clipboardService: ClipboardService,
              private cliPropertiesService: CliCorePropertiesService) {
    super();
  }

  public ngOnInit(): void {
    this.formGeneratorService.getZoneNameObservable().pipe(
      filter(value => !!value),
      tap(value => {
        const tabId = this.getTabByZoneName(value);
        if (tabId) {
          this.closeInlinePanel();
          const groupZoneID = this.getGroupZoneByZoneName(value);
          // Set the correct sidebar view based on the tab type containing the zone
          this.setSidebarViewForTab(tabId);
          this.generatedForm.redirectToTab(tabId, groupZoneID);
          this.cd.markForCheck();
        }
        this.formGeneratorService.updateZoneName(null);
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.userService.getUser().subscribe((user: User) => this.user = user);

    if (this.cookieService.getCookie(POPULATION_CRITERIA_COOKIE_NAME)) {
      this.populationCriteriaCookie = JSON.parse(this.cookieService.getCookie(POPULATION_CRITERIA_COOKIE_NAME));
    }

    this.route.queryParams
      .pipe(
        tap((qp) => {
          if (qp.tab) {
            this.initialTab = qp.tab;
            if (this.isFormLoaded && this.generatedForm) {
              const deepLinkPanel = this.DEEPLINK_PANEL_MAP[qp.tab];
              if (deepLinkPanel) {
                this.openInlinePanel(deepLinkPanel);
              } else {
                this.generatedForm.redirectToTab(qp.tab);
              }
            }
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: {tab: null},
              queryParamsHandling: 'merge',
              replaceUrl: true
            });
          }
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.route.params
      .pipe(
        tap((params: any) => {
          if (params.tab) {
            this.initialTab = params.tab;
          }
          if (params.name && params.payPeriodId && params.hasOwnProperty('isReadOnly')) {
            this.variableService.updateSelectedVariable({
              name: params.name,
              payPeriodId: +params.payPeriodId,
              isReadOnly: params.isReadOnly === 'true'
            });
          }
        }),
        filter((params: any) => {
          const contractId: number = +params.contractId || undefined;
          if (!contractId || this.contractId === contractId) {
            return false;
          }

          this.contractId = contractId;

          return true;
        }),
        switchMap(() => this.getEmployeeData(this.selectedContractDeleted)),
        catchError((err, source$) => {
          this.cd.markForCheck();

          return source$;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.selectContract({id: this.contractId}));

    merge(
      this.employeesService.employeeChanged$.asObservable(),
      this.translate.onLangChange
    ).pipe(
      debounceTime(0),
      tap(() => this.cd.detectChanges()),
      takeUntil(this.componentDestroyed$)
    )
      .subscribe();

    this.modalService.modalClosed
      .pipe(
        tap(() => {
          if (this.modalService.modalWithIdOpen.length === 0) {
            this.isModalOpened = false;
            this.cd.markForCheck();
          }
        }),
        filter((identifier: string) => {
          return identifier === IMAGE_MANAGEMENT_MODAL_ID;
        }),
        tap(() => this.cd.detectChanges()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.modalService.modalOpened
      .pipe(
        tap(() => {
          this.isModalOpened = true;
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.employeesUtilsService.contractsDeleted$.asObservable()
      .pipe(
        filter(Boolean),
        tap((contractIdsDeleted: number[]) => this.removeDeletedContracts(contractIdsDeleted)),
        tap(() => this.handleRedirection()),
        switchMap(() => {
          if (!this.selectedContractDeleted) {
            return this.getEmployeeData(true);
          } else {
            return EMPTY;
          }
        }),
        tap(() => this.selectContract({id: this.contractId})),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    merge(
      this.logEventService.logEventsRefreshed$.asObservable(),
      this.sdconnectService.sdcLogsRefreshed$.asObservable()
    ).pipe(
      switchMap(() => this.sdconnectService.getSdcStatus(this.contractId)),
      tap((sdcStatus: SdcStatusDTO) => this.selectedContractSdcStatus = sdcStatus),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.employeesService.getEmployeeFileActionButtonsDisabled()
      .pipe(
        tap((isEmployeeFileActionButtonsDisabled) => {
          if (isEmployeeFileActionButtonsDisabled.action === 'aiOCRResponseLoading') {
            this.isActionButtonDisabled = isEmployeeFileActionButtonsDisabled.value;
            this.cd.markForCheck();
          }
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.setHrCalendarFeature();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isPeopleRead) {
      this._isPeopleRead = this.isPeopleRead;
    }
    if (changes.contractId) {
      this.getEmployeeData()
        .subscribe(() => this.selectContract({id: this.contractId}));
    }

    this.logEventService.logEventStatusModified.pipe(
      tap(() => this.load()),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscribeIsActiveCardChanged();
    this.activeCardSubject.unsubscribe();
  }

  private setHrCalendarFeature(): void {
    this.cliPropertiesService.get()
      .pipe(
        tap((properties) => {
          this.hrCalendarEnabled = properties.features?.includes(FeatureEnum.HR_CALENDAR) ?? false;
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public canDeactivate(): any | Observable<any> {

    if (this.formIsPristine) {
      return of(true);
    }

    return from(
      this.modalService.dynamicConfirmModal({
        'title': 'DIALOG.you_have_unsaved_changes',
        'text': 'DIALOG.you_have_unsaved_changes?',
        'confirmButton': 'FORMS.discard_all_changes',
        'cancelButton': 'FORMS.keep_editing'
      })
    ).pipe(
      switchMap(choice => choice === true
        ? this.fileChangesService.discardPendingFileChanges(this.fileChanges).pipe(map(() => true))
        : of(false)),
      tap(confirmed => {
        if (confirmed) {
          this.generatedForm.form.markAsPristine();
          this.variableTab?.markAsDirty(false);
          this.timeTab?.resetChanges();
        }
      }),
      catchError(() => of(false)),
      takeUntil(this.componentDestroyed$)
    );
  }

  public dataHasEmptyValues(data: ZoneValues) {
    return Object.values(data.values).every(dataValue => isValueConsideredAsEmpty(dataValue.value));
  }

  /**
   * Update the edited contract, contract data and employee. This is called upon save on the generated employee form.
   */
  public updateEmployeeAndContract(skipLoad = false): void {
    let contractDataHasChanged = false;

    this.blockingErrors = this.generatedForm.checkBlockingError();

    if (this.blockingErrors.length) {
      this.modalService.openModalWithId('blockingErrorModal');
      return;
    }

    if (this.saving) {
      return;
    }
    this.saving = true;

    const dataMap = this.generatedForm.getModifiedCardsData();

    const contractData: { [group: string]: any } = this.fetchContractData(dataMap);
    const contractValues = Object.values(contractData);

    /**
     * IF you want to save a groupZone with data id undefined and no values (This is the case of a delete all datas
     * in a groupZone) you don't send a data empty for that groupZone but an empty array so the BE knows that
     * it has to do a delete
     */

    contractValues.forEach((data: ZoneValues[]) => {
      const groupZoneName = data[0].groupName;
      data = data.filter(value => !(!value.id && this.dataHasEmptyValues(value)));
      contractData[groupZoneName] = data;
    });


    const requests: Observable<any>[] = [];
    if (Object.keys(contractData).length) {
      contractDataHasChanged = true;
      requests.push(
        this.fileChangesService.saveWithPendingFileChanges(this.fileChanges,
          this.employeesService.updateContractData(this.selectedContract.id, contractData))
          .pipe(
            tap(() => {
              if (this.generatedForm && this.generatedForm.form) {
                this.generatedForm.form.markAsPristine();
              }
              if (!skipLoad) {
                this.load();
              }
            }),
            switchMap(result => this.employeesService.getCliEmp(this.contractId).pipe(
              tap((response: any) => {
                this.employee = new Employee(response.data);
                this.contracts = this.employee.contracts;
                this.employeesService.editListEmployee.emit(response.data);
              }),
              map(() => result),
              catchError(() => of(result))
            ))
          )
      );
    }
    if (this.isTimeTabDirty) {
      requests.push(this.timeTab.saveChanges());
    }

    if (this.isVariableTabDirty) {
      requests.push(this.variableTab.save());
    }

    forkJoin(requests)
      .pipe(
        tap(result => {
          this.employeesService.contractChanged$.next(this.contractId);
          const updateContractDataResult = result[0];
          if (contractDataHasChanged && !updateContractDataResult.isInPopulation) {
            const redirectionUrlArray = window.location.href.split('/');
            window.location.href = redirectionUrlArray.slice(0, redirectionUrlArray.length - 1).join('/');
            window.location.reload();
          }
        }),
        catchError(() => EMPTY),
        finalize(() => this.saving = false),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public simulateCalculation(): void {
    if (!this.hasSimulationDependencies()) {
      return;
    }
    const dataMap: ZoneValuesByGroup = this.generatedForm.getModifiedCardsData();
    const contractData: SimulationContractData = this.fetchContractData(dataMap);
    this.clearAutoCalculatedValues(contractData);

    this.employeesService
      .simulateCalculation(this.selectedContract.id, contractData)
      .pipe(
        map((response: ZoneValues[]) => this.groupByTab(response)),
        tap((result: SimulationResponseByTab) => this.generatedForm.simulationFill(result)),
        catchError(() => EMPTY),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private clearAutoCalculatedValues(contractData: SimulationContractData): void {
    const autoZonesByGroup = this.generatedForm.getAutoCalculatedZonesByGroup();

    Object.entries(contractData).forEach(([group, entries]) => {
      const autoZones = autoZonesByGroup[group];
      if (!autoZones || !entries) {
        return;
      }

      entries.forEach(entry => {
        if (!entry.values) {
          return;
        }
        autoZones.forEach(zoneId => {
          if (entry.values[zoneId]) {
            entry.values[zoneId] = { ...entry.values[zoneId], value: '', transId: null };
          }
        });
      });
    });
  }

  private groupByTab(entries: ZoneValues[]): SimulationResponseByTab {
    const result: SimulationResponseByTab = {};

    entries.forEach((entry: ZoneValues) => {
      const zoneIds = Object.keys(entry.values ?? {});
      const tab = zoneIds
        .map((zoneId: string) => this.zonesByTab.get(zoneId))
        .find(Boolean);

      if (!tab) {
        return;
      }

      if (!result[tab]) {
        result[tab] = {};
      }
      if (!result[tab][entry.groupName]) {
        result[tab][entry.groupName] = [];
      }

      result[tab][entry.groupName].push(entry);
    });

    return result;
  }

  public fetchContractData(dataMap: ZoneValuesByGroup): SimulationContractData {
    return Object.keys(dataMap)
      .reduce((memo: SimulationContractData, item: string) => {
        memo[item] = dataMap[item].map((datum: ZoneValues) => {

          const data: SimulationContractZoneValue = {
            begindate: datum.begindate,
            isDated: datum.isDated,
            enddate: datum.enddate,
            groupName: datum._groupName || datum.groupName,
            default: datum.default,
            id: datum.id
          };

          const values = datum._values || datum.values;
          const zoneNames = this.getDescriptorZoneNames(data.groupName);
          if (values) {
            data.values = Object.keys(values)
              .filter(zone => zoneNames.includes(zone))
              .reduce((value: ZoneValues['values'], key: string) => {
                value[key] = values[key];
                if (typeof value[key].value === 'string') {
                  value[key].value = value[key].value.trim();
                }
                if (value[key].value === null) {
                  value[key].value = '';
                }

                return value;
              }, {} as ZoneValues['values']);
          }

          return data;
        });
        return memo;
      }, {} as SimulationContractData);
  }

  public getContractDate(contract: Contract): string {
    return `(${
      this.dateLocalizationPipe.transform(contract.begindate)
    } - ${
      contract.enddate === DATE_MAX || !contract.enddate
        ? '...'
        : this.dateLocalizationPipe.transform(contract.enddate)
    })`;
  }

  public formLoaded(): void {
    this.subscribeOnChange();
    this.isFormLoaded = true;
    // Some former tabs (Time, ContractAlerts, EventsLog, ...) are now inline panels:
    // open the matching panel when deep-linked instead of relying on a form tab.
    const deepLinkPanel = this.DEEPLINK_PANEL_MAP[this.initialTab];
    if (deepLinkPanel) {
      this.openInlinePanel(deepLinkPanel);
    }
    this.maybeStartSidebarTour();

    this.generatedForm.form.valueChanges
      .pipe(
        debounceTime(500),
        pairwise(),
        map(([prev, curr]) => this.getDiff(prev, curr)),
        filter((diff: SimulationDiff[]) => diff.length > 0),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((diff: SimulationDiff[]) => {
        if (this.canRunSimulation(diff)) {
          this.simulateCalculation();
        }
      });

    this.employeesService.getRecalculationRequested()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.simulateCalculation());
  }

  private getDiff(obj1: FormValueNode, obj2: FormValueNode, path = ''): SimulationDiff[] {
    let changes: SimulationDiff[] = [];

    Object.keys({ ...obj1, ...obj2 }).forEach(key => {
      const fullPath = path ? `${path}.${key}` : key;

      const left = obj1[key];
      const right = obj2[key];

      if (left !== right) {
        if (
          typeof left === 'object' &&
          typeof right === 'object' &&
          left &&
          right
        ) {
          changes = changes.concat(this.getDiff(left as FormValueNode, right as FormValueNode, fullPath));
        } else {
          changes.push({
            path: fullPath,
            zoneId: key,
            before: left,
            after: right,
          });
        }
      }
    });

    return changes;
  }

  /**
   * Starts the first-time guided tour introducing the new sidebar: the former special tabs are
   * now inline panels, and the remaining tabs are split into "Contract details" and "Employee
   * details". Shown only once per browser (see GuidedTourService).
   */
  private maybeStartSidebarTour(): void {
    if (this.isSkillModule) {
      return;
    }
    // Defer so the sidebar buttons are rendered before the tour anchors to them.
    setTimeout(() => {
      const steps: GuidedTourStep[] = [
        {
          selector: '[data-tour="emp-sidebar"]',
          titleKey: 'GUIDED_TOUR.EMPLOYEE.sidebar_title',
          textKey: 'GUIDED_TOUR.EMPLOYEE.sidebar_text'
        },
        {
          selector: '[data-tour="emp-contract-details"]',
          titleKey: 'GUIDED_TOUR.EMPLOYEE.contract_title',
          textKey: 'GUIDED_TOUR.EMPLOYEE.contract_text'
        },
        {
          selector: '[data-tour="emp-employee-details"]',
          titleKey: 'GUIDED_TOUR.EMPLOYEE.employee_title',
          textKey: 'GUIDED_TOUR.EMPLOYEE.employee_text'
        },
        {
          // Highlight the whole quick-access panel (all the special-panel icons), not just the first one.
          getRect: () => this.getQuickAccessPanelRect(),
          titleKey: 'GUIDED_TOUR.EMPLOYEE.panels_title',
          textKey: 'GUIDED_TOUR.EMPLOYEE.panels_text'
        },
        {
          // When the contract has alerts, highlight the alerts icon; otherwise fall back to the
          // whole quick-access panel so the step is always shown and never points at empty space.
          getRect: () => this.getAlertsStepRect(),
          titleKey: 'GUIDED_TOUR.EMPLOYEE.alerts_title',
          textKey: 'GUIDED_TOUR.EMPLOYEE.alerts_text',
          // The alerts icon sits in a scrollable sidebar and may be below the fold: scroll it into
          // view first so the tour never anchors to an off-screen element.
          beforeShow: () => this.scrollAlertsIntoViewForTour()
        }
      ];

      // Only introduce the group-zone selector when a tab actually has the selector arrow (i.e. a
      // tab with more than one group zone). Without an arrow the dropdown cannot be opened, so these
      // two steps would have no target to highlight — skip them entirely instead of showing them.
      if (this.findVisibleGroupZoneSelector()) {
        steps.push(
          {
            selector: '[data-tour="tab-groupzone-selector"]',
            titleKey: 'GUIDED_TOUR.EMPLOYEE.groupzone_title',
            textKey: 'GUIDED_TOUR.EMPLOYEE.groupzone_text'
          },
          {
            // Opens the group-zone selector dropdown so we can explain that clicking a section
            // jumps straight to it. The dropdown is closed again when leaving the step.
            selector: '.tab-dropdown',
            titleKey: 'GUIDED_TOUR.EMPLOYEE.groupzone_open_title',
            textKey: 'GUIDED_TOUR.EMPLOYEE.groupzone_open_text',
            beforeShow: () => this.openGroupZoneSelectorForTour(),
            onLeave: () => this.generatedForm?.closeDropdown()
          }
        );
      }

      this.guidedTourService.startOnce('employeeFormSidebar', steps);
    });
  }

  /**
   * Opens the group-zone selector dropdown of the first visible tab that has one, so the guided
   * tour can highlight it. No-op when no such selector is visible (the step then auto-skips).
   */
  private openGroupZoneSelectorForTour(): void {
    const chevron = this.findVisibleGroupZoneSelector();
    if (chevron && this.generatedForm && !this.generatedForm.openDropdownTabId) {
      chevron.click();
    }
  }

  /** Returns the rendered (non-zero size) group-zone selector arrow, or null when no tab has one. */
  private findVisibleGroupZoneSelector(): HTMLElement {
    return Array.from(document.querySelectorAll('[data-tour="tab-groupzone-selector"]'))
      .find((el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }) as HTMLElement || null;
  }

  /**
   * Returns the viewport rectangle covering the whole quick-access panel (all the visible
   * special-panel sidebar icons), so the guided tour highlights the entire group rather than a
   * single icon. Returns null when none are visible (the step then auto-skips).
   */
  private getQuickAccessPanelRect(): { top: number; left: number; width: number; height: number } | null {
    const rects = Array.from(document.querySelectorAll('[data-tour-group="emp-quick-access"]'))
      .map((el: HTMLElement) => el.getBoundingClientRect())
      .filter(rect => rect.width > 0 && rect.height > 0);
    if (!rects.length) {
      return null;
    }
    const top = Math.min(...rects.map(rect => rect.top));
    const left = Math.min(...rects.map(rect => rect.left));
    const right = Math.max(...rects.map(rect => rect.right));
    const bottom = Math.max(...rects.map(rect => rect.bottom));
    return {top, left, width: right - left, height: bottom - top};
  }

  /**
   * Target rectangle for the alerts step: the alerts icon when the contract has alerts, otherwise
   * the whole quick-access panel so the step is still shown when there is no alerts icon.
   */
  private getAlertsStepRect(): { top: number; left: number; width: number; height: number } | null {
    if (this.hasContractAlerts) {
      const alertsIcon = this.findVisibleAlertsIcon();
      if (alertsIcon) {
        const rect = alertsIcon.getBoundingClientRect();
        return {top: rect.top, left: rect.left, width: rect.width, height: rect.height};
      }
    }
    return this.getQuickAccessPanelRect();
  }

  /** Returns the rendered (non-zero size) alerts sidebar icon, or null when it is not present. */
  private findVisibleAlertsIcon(): HTMLElement {
    return Array.from(document.querySelectorAll('[data-tour="emp-alerts"]'))
      .find((el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }) as HTMLElement || null;
  }

  /**
   * Scrolls the alerts icon into view inside the scrollable sidebar before the tour's alerts step
   * is shown, so the spotlight never anchors to an element that is below the visible area.
   */
  private scrollAlertsIntoViewForTour(): void {
    if (!this.hasContractAlerts) {
      return;
    }
    this.findVisibleAlertsIcon()?.scrollIntoView({block: 'center', inline: 'nearest'});
  }

  /**
   * Extract the current active value of a zone from the form values.
   * For dated group zones, picks the record where today falls between begindate and enddate.
   * Returns the transId (display text) of the zone value, or null if empty.
   */
  private extractPositionAndLocation(): void {
    this.employeePosition = null;
    this.employeeLocation = null;
    this.employeeBusinessUnit = null;

    if (!this.values) {
      return;
    }

    const allRecords = Array.from(this.values.values()).reduce((acc, records) => acc.concat(records || []), []);
    this.employeePosition = this.employeesUtilsService.extractZoneValue(allRecords, 'Position');
    this.employeeLocation = this.employeesUtilsService.extractZoneValue(allRecords, 'Workplace');
    this.employeeBusinessUnit = this.employeesUtilsService.extractZoneValue(allRecords, 'OrganizationalUnit');

    this.cd.markForCheck();
  }


  public unsubscribeIsActiveCardChanged() {
    this.activeCardSubject.next(true);
  }

  public getEmployeeData(fetchEmployee = false): Observable<any> {
    this.selectedContractDeleted = false;

    if (this.contractId) {
      this.selectedContract = undefined;
      this.hrCalendarWorkSchedules = [];
      this.formDescriptor = undefined;

      if (!fetchEmployee && this.employee && this.employee.contracts.find(c => c.id === this.contractId)) {
        return of(true);
      } else {
        this.employee = null;
        this.contracts = [];

        this.loading = true;
        this.cd.detectChanges();

        return this.employeesService.getCliEmp(this.contractId)
          .pipe(
            finalize(() => this.loading = false),
            map((response: any) => {
              this.employee = new Employee(response.data);
              this.contracts = this.employee.contracts;
              this.employeesService.selectedEmployeeContracts.next(this.contracts);
              return this.contractId;
            })
          );
      }
    } else {
      return EMPTY;
    }
  }

  private load(): void {
    this.unsubscribeIsActiveCardChanged();
    this.loadingForm = true;
    this.cd.detectChanges();

    forkJoin([this.employeesService.getContract(this.contractId), this.sdconnectService.getSdcStatus(this.contractId)])
      .pipe(
        switchMap(([contract, sdcStatus]) => {
          const existContract = this.contracts?.find((c: EmpContractAttributes) => c.id === this.contractId) || {};
          Object.assign(existContract, contract);
          this.selectedContract = existContract as EmpContractAttributes;
          this.hrCalendarWorkSchedules = [];
          this.selectedContractSdcStatus = sdcStatus;
          this.contractBeginDate = strToDate(this.selectedContract.begindate);
          this.formDescriptor = undefined;
          this.cliRegId = this.selectedContract.cliRegID;
          this.isPeopleRead = this._isPeopleRead || this.selectedContract.readOnly;

          if (this.router.isActive('employees/files', false)) {
            this.router.navigate(['employees/files', this.contractId], {queryParamsHandling: 'preserve'});
          }

          if (this.router.isActive('employees/skills', false)) {
            this.router.navigate(['employees/skills', this.contractId], {queryParamsHandling: 'preserve'});
          }

          if (!this.cliRegId) {
            return EMPTY;
          }

          return this.withFormDescriptor(this.cliRegId);
        }),
        map((descriptor: FormDescriptor) => {
          if (this.loading) {
            return;
          }

          this.addDescriptorMetadatas(descriptor);
          this.formDescriptor = descriptor;

          const calcDeps =
            (this.formDescriptor?.tabSheets ?? [])
              .reduce((acc: string[], sheet) => {
                (sheet.groupZones ?? []).forEach(gz => {
                  (gz.zones ?? []).forEach(zone => {
                    const d = zone?.calculation?.dependencies;
                    if (Array.isArray(d)) {
                      acc.push(...d);
                    }
                  });
                });
                return acc;
              }, []);

          this.allCalculationDependencies = Array.from(
            new Set(
              calcDeps.filter(d => typeof d === 'string' && d.trim().length > 0)
            )
          );

          this.hasAttachmentPanel = this.isFileZoneExist(descriptor);
          // Set default sidebar view based on available tab types.
          // By default, "Contract details" should be selected when the user arrives.
          if (!this.activeSidebarView) {
            if (descriptor.tabSheets.some(t => t.tabType === 'CONTRACT_TAB' || !t.tabType)) {
              this.activeSidebarView = 'contract';
            } else if (descriptor.tabSheets.some(t => t.tabType === 'EMP_TAB' || t.tabType === 'EMP_SKILL_TAB')) {
              this.activeSidebarView = 'employee';
            }
          }
          // Make sure the tab selected by default matches the active sidebar view, so that
          // e.g. the first "Contract details" tab is shown instead of the first "Employee details" tab.
          this.resolveInitialTabForView(descriptor);
          const transformer = new DataTransformer(descriptor, 'empDatas', this.formGeneratorService);
          this.values = transformer.transform(this.selectedContract);
          this.hrCalendarWorkSchedules = HrCalendarWorkScheduleUtil
            .fromContractValues(this.values, <HrCalendarContractPeriod>this.selectedContract);
          this.extractPositionAndLocation();
          this.checkContractAlerts();
          this.hasEventErrors = !!this.selectedContract?.attributes?.hasEventErrors;
          this.updateSdcInlinePanelsVisibility();
          if (this.isInlinePanelOpen) {
            this.refreshActiveInlinePanel();
          }
          this.loadingForm = false;
        }),
        switchMap(() => this.employeesService.getEmpFiles(this.contractId)),
        finalize(() => {
          this.loadingForm = false;
          this.cd.detectChanges();
        })
      )
      .subscribe();
  }

  private subscribeOnChange(): void {
    this.inheritedGroupZonesMap.forEach((inheritedGroupZone) => {
      const editGroupZonesMap: Map<string, any> = inheritedGroupZone.editGroupZonesMap;
      const groupZone: GroupZoneDescriptor = inheritedGroupZone.groupZone;

      inheritedGroupZone.path
        .reduce((prev, curr) => prev.get(curr), this.generatedForm.form)
        .valueChanges
        .pipe(
          takeUntil(this.componentDestroyed$),
          debounceTime(200),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
          switchMap(() => {
            const modifiedData: { [card: string]: ZoneValues[] } = this.generatedForm.getModifiedCardsData();
            const modifiedRepInheritedData = {};
            if (modifiedData[groupZone.groupZoneID]) {
              modifiedRepInheritedData[groupZone.groupZoneID] = modifiedData[groupZone.groupZoneID];
            }

            if (!Object.keys(modifiedRepInheritedData).length) {
              if (this.repInheritedDirtyDataByGroupZone.get(groupZone.groupZoneID)) {
                // Data were dirty but they are not anymore
                // Preview and set dataDirty flag to false if we are going to preview (we are back to original state)
                this.repInheritedDirtyDataByGroupZone.set(groupZone.groupZoneID, false);
                return this.employeesService.previewEmpContract(this.contractId, {});
              } else {
                return EMPTY;
              }
            }

            const contractDataForPreview: { [group: string]: any } = this.fetchContractData(modifiedData);
            // Set flag to true => when we get back to initial data state, still launch preview
            this.repInheritedDirtyDataByGroupZone.set(groupZone.groupZoneID, true);
            return this.employeesService.previewEmpContract(this.contractId, contractDataForPreview);
          })
        )
        .subscribe((data: any[]) => {
          this.onInheritedChange(editGroupZonesMap, data);
        });
    });
  }

  private onInheritedChange(editGroupZonesMap: Map<string, any>, data: any[]) {
    for (const editGroupZone of editGroupZonesMap.values()) {
      const groupValues: ZoneValues[] = this.values.get(editGroupZone.groupZone.groupZoneID);
      if (!groupValues) {
        return;
      }
      groupValues.splice(0, groupValues.length);
    }

    data.forEach((groupData) => {
      const groupValues = this.values.get(groupData.groupName);
      const groupZoneInfos = editGroupZonesMap.get(groupData.groupName);

      if (groupValues && groupZoneInfos) {
        groupValues.push(groupData);
        groupZoneInfos.groupZone.card.loadFields();
      }
    });

    editGroupZonesMap.forEach((editGroupZone) => {
      const groupValues: any[] = this.values.get(editGroupZone.groupZone.groupZoneID);
      if (groupValues && !groupValues.length) {
        groupValues.push(this.formGeneratorService.createEmptyData(editGroupZone.groupZone)[0]);
      }

      if (editGroupZone.groupZone.card) {
        editGroupZone.groupZone.card.loadFields();
      }
      if (editGroupZone.groupZone.table) {
        editGroupZone.groupZone.table.loadRows();
      }
    });
    this.cd.markForCheck();
  }

  private withFormDescriptor(cliRegId: number): Observable<FormDescriptor> {
    const descriptor$ = this.descriptors.has(cliRegId) ? of(this.descriptors.get(cliRegId)) :
      this.isSkillModule
        ? this.formDescriptorService.getEmployeeSkillsFormDescriptor(cliRegId)
        : this.formDescriptorService.getFormDescriptor(cliRegId);

    return descriptor$.pipe(
      tap((descriptor) => {
        this.zonesByTab = this.extractZonesByTab(descriptor);
        this.descriptors.set(cliRegId, descriptor);
        if (!this.isSkillModule) {
          this.addOrRemoveSpecialTabs(descriptor);
        }
      })
    );
  }

  private extractZonesByTab(descriptor: FormDescriptor): Map<string, string> {
    const zoneByTab = new Map<string, string>();
    descriptor.tabSheets.forEach(tabSheet => {
      tabSheet.groupZones.forEach(groupZone => {
        groupZone.zones.forEach(zone => {
          zoneByTab.set(zone.zoneID, tabSheet.tabSheetID);
        });
      });
    });
    return zoneByTab;
  }


  public onTabSelected(tabSheetDescriptor: TabSheetDescriptor): void {
    // Save for next time (i.e. an other component instance for an other employee...)
    initialTab = tabSheetDescriptor.tabSheetID;

    switch (initialTab) {
      case 'ChangeTracking':
        this.openInlinePanel(this.INLINE_PANEL.CHANGE_TRACKING);
        break;
      case 'Attachments':
        this.openInlinePanel(this.INLINE_PANEL.ATTACHMENTS);
        break;
      case 'VariableElements':
        this.variableTab = this.generatedForm.tabs
          .find(tab => tab.instance.descriptor.tabSheetID === 'VariableElements').instance.variableElementsComponent;

        this.isVariableReadOnly = this.readOnly
          || this.selectedContract.readOnly
          || !this.rolesService.hasRole('VARIABLE_PEOPLE_WRITE');

        if (this.variableTab.contract !== this.selectedContract) {
          Object.assign(this.variableTab, {
            contract: this.selectedContract,
            cliRegId: this.selectedContract.cliRegID,
            // isReadOnly: false
            isReadOnly: this.isVariableReadOnly
          });
          this.variableTab.ngOnChanges({contract: this.variableTab.contract});
        }
        break;
      case 'Time':
        this.openInlinePanel(this.INLINE_PANEL.TIME);
        break;
      case 'SdcPersonForm':
        this.openInlinePanel(this.INLINE_PANEL.SDC_PERSON);
        break;
      case 'SdcPayrollForm':
        this.openInlinePanel(this.INLINE_PANEL.SDC_PAYROLL);
        break;
      default:
        break;
    }

    // Save for this time too (when saving, the form is reloaded).
    this.initialTab = initialTab;
  }

  /**
   * Returns true if SDC3 features should be displayed.
   * V3 is enabled and either V2 is not enabled or the user has the SDC3_ADMIN role.
   */
  private shouldDisplaySDC3(hasSDC3AdminRole = this.rolesService.hasRole('SDC3_ADMIN')): boolean {
    const v2 = this.selectedContractSdcStatus?.personMasterDataSyncOnSDC2Activated;
    const v3 = this.selectedContractSdcStatus?.personMasterDataSyncOnSDC3Activated;
    return v3 && (!v2 || hasSDC3AdminRole);
  }

  private addOrRemoveSpecialTabs(descriptor: FormDescriptor): void {
    if (descriptor.tabSheets) {
      // High position so the special tab is appended after all standard tabs.
      this.addOrRemoveSpecialTab(descriptor, 'VariableElements', 1000, 'PAYROLL_PREPARE.variable_payroll',
        false, ['VARIABLE_PEOPLE_READ', 'VARIABLE_PEOPLE_WRITE']);
    }
  }

  /**
   * Computes the visibility of the SDConnect inline-panel sidebar icons. SDConnect and
   * SDConnect Person are no longer dedicated form tabs but inline panels, so their icons are
   * shown/hidden based on the feature toggle and the contract SDC status rather than by
   * adding/removing special tabs.
   */
  private updateSdcInlinePanelsVisibility(): void {
    this.sdConnectEnabled = localStorage.getItem('gp_sdconnect_enabled') === 'true';
    const hasSDC3AdminRole = this.rolesService.hasRole('SDC3_ADMIN');
    this.sdcPersonEnabled = this.sdConnectEnabled && this.shouldDisplaySDC3(hasSDC3AdminRole);
    this.sdcPayrollEnabled = this.sdConnectEnabled
      && !!this.selectedContractSdcStatus?.payrollInputSyncActivated
      && hasSDC3AdminRole;
  }

  private addOrRemoveSpecialTab(descriptor: FormDescriptor,
                                tabSheetID: string, tabSheetPosition: number, transId: string, error = false,
                                roles: string[] = [],
                                add = true): void {
    const index = descriptor.tabSheets.findIndex(tabSheet => tabSheet.tabSheetID === tabSheetID);
    if (index !== -1) {
      descriptor.tabSheets.splice(index, 1);
    }
    if (add && (roles.length <= 0 || this.rolesService.hasRole(...roles))) {
      descriptor.tabSheets.push(createTabSheetDescriptor(tabSheetID, tabSheetPosition, transId, [], transId, error));
    }
  }

  private getChangeTrackingType(): string {
    let query = `(itemId==${this.contractId};cliCoreId==${this.user.cliCoreId};(item==CONTRACT_FLATTEN,item==EMP_DATA,item==PEOPLE_TREE_NODE,item==CLI_EMP_DATA`;
    if (this.rolesService.hasRole('ABSENCE_PEOPLE_READ', 'ABSENCE_PEOPLE_WRITE')) {
      query += ',item==EMP_ABSENCE';
    }
    if (this.rolesService.hasRole('VARIABLE_PEOPLE_READ', 'VARIABLE_PEOPLE_WRITE')) {
      query += ',item==EMP_VARIABLE';
    }

    query += '))';

    return query;
  }

  public confirmResetForm(): void {
    from(this.modalService.dynamicConfirmModal({
      'title': 'DIALOG.discard_pending_changes',
      'text': 'DIALOG.discard_pending_changes?',
      'confirmButton': 'FORMS.discard_all_changes',
      'cancelButton': 'FORMS.keep_editing'
    })).pipe(
      switchMap(confirmed => confirmed ? this.fileChangesService.discardPendingFileChanges(this.fileChanges) : EMPTY),
      tap(() => {
        this.generatedForm.form.markAsPristine();
        this.load();
      }),
      catchError(() => EMPTY),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public selectContract(contract: Data<EmpContractAttributes>): void {
    const contractId = contract.id;

    if (isPresent(this.selectedContract) && this.selectedContract.id === contractId) {
      return;
    }

    this.canDeactivate()
      .subscribe((canDeactivate: boolean) => {
        if (!canDeactivate) {
          const tmpContract = this.selectedContract;
          this.selectedContract = {};
          this.cd.detectChanges();
          this.selectedContract = tmpContract;
          this.cd.markForCheck();
        } else {
          this.fileChangesService.discardPendingFileChanges(this.fileChanges)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe();
          this.contractId = contractId;
          this.load();
        }
      });
  }

  public toggleTooltip(event: MouseEvent, isCopied: boolean) {
    event.stopPropagation();
    this.isCopied = isCopied;
  }

  public copyEmailToClipboard(event: MouseEvent): void {
    this.clipboardService.copy(this.employee?.email);
    this.toggleTooltip(event, true);
  }

  public hasPhoto(): boolean {
    return !!this.employee?.photoLink?.href;
  }

  public getInitials(): string {
    return getInitials(this.employee?.firstName, this.employee?.lastName);
  }

  public showImageCropper(): void {
    if (!this.isSkillModule && !this.readOnly && !this.isPeopleRead) {
      this.modalService.openModalWithName(IMAGE_MANAGEMENT_MODAL_ID)
        .then((modelRef: ComponentRef<ImageManagementComponent>) => {
          modelRef.instance.title = 'FORMS.change_employee_photo';
          modelRef.instance.imageSize = {width: 200, height: 200};
          modelRef.instance.photoLink = this.employee?.photoLink?.href;
          modelRef.instance.isCropperEnabled = true;

          modelRef.instance.imageUploadCallBack = this.imageUpload.bind(this);
          modelRef.instance.imageUploadSuccessCallBack = this.refreshEmpData.bind(this);

          modelRef.instance.imageDeleteCallBack = this.imageDelete.bind(this);
          modelRef.instance.imageDeleteSuccessCallBack = this.setEmpPhotoLinkToDefault.bind(this);
        });
    }
  }

  public refreshEmpData() {
    this.employeesService.employeeChanged$.next(this.employee);
  }

  public setEmpPhotoLinkToDefault() {
    this.employee.setPhotoUrl('');
    this.refreshEmpData();
  }

  public imageUpload(image): Observable<unknown> {
    return this.employeesService.changePicture(this.employee, image);
  }

  public imageDelete(): Observable<unknown> {
    return this.employeesService.deletePicture(this.employee);
  }

  public massUpdateMode() {
    this.router.navigate(
      [
        this.initialTab.match(/variable/i)
          ? '/mass-data-update/variable-payroll'
          : '/mass-data-update/time-attendance'
      ],
      {queryParams: {emp: this.employee.id, clireg: this.selectedContract.cliRegID}}
    );
  }


  public closeInlinePanel(): void {
    this.isInlinePanelOpen = false;
    this.activeInlinePanel = null;
    this.inlinePanelHeightPx = null;
  }

  public showDataPanel(): void {
    this.closeInlinePanel();
    this.activeSidebarView = null;
    // Activate the first descriptor tab
    if (this.generatedForm?.tabs?.length) {
      const dataTab = this.generatedForm.tabs[0];
      if (dataTab) {
        this.generatedForm.activateTab(dataTab.instance);
      }
    }
  }

  public showEmployeeDetailsPanel(): void {
    this.closeInlinePanel();
    this.activeSidebarView = 'employee';
    this.cd.markForCheck();
    // Activate the first EMP_TAB/EMP_SKILL_TAB tab
    if (this.generatedForm?.tabs?.length) {
      const empTab = this.generatedForm.tabs.find(
        t => {
          const tabType = t.instance.descriptor.tabType;
          return tabType === 'EMP_TAB' || tabType === 'EMP_SKILL_TAB';
        }
      );
      if (empTab) {
        this.generatedForm.activateTab(empTab.instance);
      }
    }
  }

  public showContractDetailsPanel(): void {
    this.closeInlinePanel();
    this.activeSidebarView = 'contract';
    this.cd.markForCheck();
    // Activate the first CONTRACT_TAB tab, or a tab without a type (like payroll) if no CONTRACT_TAB exists
    if (this.generatedForm?.tabs?.length) {
      const contractTab = this.generatedForm.tabs.find(
        t => t.instance.descriptor.tabType === 'CONTRACT_TAB'
      );
      if (contractTab) {
        this.generatedForm.activateTab(contractTab.instance);
      } else {
        // If no CONTRACT_TAB, find first tab without a type
        const noTypeTab = this.generatedForm.tabs.find(
          t => !t.instance.descriptor.tabType
        );
        if (noTypeTab) {
          this.generatedForm.activateTab(noTypeTab.instance);
        }
      }
    }
  }

  /**
   * Ensures the tab selected by default is consistent with the active sidebar view.
   * Preserves an explicitly requested tab when it belongs to the active view or when it
   * targets an inline panel (e.g. Time) that is not part of the form descriptor.
   * @param descriptor The form descriptor being loaded
   */
  private resolveInitialTabForView(descriptor: FormDescriptor): void {
    if (!this.activeSidebarView || !descriptor?.tabSheets?.length) {
      return;
    }

    const requestedTab = this.initialTab
      ? descriptor.tabSheets.find(tab => tab.tabSheetID === this.initialTab)
      : null;

    // Keep deep-links to inline-panel tabs (e.g. Time) that are not real form tabs.
    if (this.initialTab && !requestedTab) {
      return;
    }

    const requestedTabMatchesView = requestedTab && this.tabBelongsToView(requestedTab, this.activeSidebarView);
    if (!requestedTabMatchesView) {
      this.initialTab = this.getFirstTabIdForView(descriptor, this.activeSidebarView);
    }
  }

  /**
   * Whether the given tab belongs to the provided sidebar view.
   */
  private tabBelongsToView(tab: TabSheetDescriptor, view: 'employee' | 'contract'): boolean {
    return view === 'employee'
      ? tab.tabType === 'EMP_TAB' || tab.tabType === 'EMP_SKILL_TAB'
      // CONTRACT_TAB or tabs without a type (like payroll) are treated as contract tabs
      : tab.tabType === 'CONTRACT_TAB' || !tab.tabType;
  }

  /**
   * Returns the id of the first tab matching the provided sidebar view, or null if none.
   * Prefers a typed tab for the view (e.g. CONTRACT_TAB) before falling back to a tab
   * without a type (like payroll).
   */
  private getFirstTabIdForView(descriptor: FormDescriptor, view: 'employee' | 'contract'): string {
    const orderedTabs = [...descriptor.tabSheets].sort((a, b) => a.tabSheetPosition - b.tabSheetPosition);
    const typedTab = orderedTabs.find(tab => view === 'employee'
      ? tab.tabType === 'EMP_TAB' || tab.tabType === 'EMP_SKILL_TAB'
      : tab.tabType === 'CONTRACT_TAB');
    const matchingTab = typedTab || orderedTabs.find(tab => this.tabBelongsToView(tab, view));
    return matchingTab ? matchingTab.tabSheetID : null;
  }

  /**
   * Sets the correct sidebar view (employee or contract) based on the tab type
   * @param tabId The ID of the tab to check
   */
  private setSidebarViewForTab(tabId: string): void {
    if (!this.formDescriptor) {
      return;
    }

    const tabSheet = this.formDescriptor.tabSheets.find(t => t.tabSheetID === tabId);
    if (!tabSheet) {
      return;
    }

    // Determine if this is an employee or contract tab.
    // CONTRACT_TAB or tabs without a type (like payroll) are treated as contract tabs.
    this.activeSidebarView = this.tabBelongsToView(tabSheet, 'employee') ? 'employee' : 'contract';
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    if (this.isInlinePanelOpen) {
      this.updateInlinePanelViewportHeight();
    }
  }

  public get activeInlinePanelTitleKey(): string {
    return this.INLINE_PANEL_TITLE_MAP[this.activeInlinePanel] || '';
  }

  public onChangeTrackingInit(initFn: (entityId: number, entityType: string, itemType?: string) => void): void {
    // Only store the init function. The actual data load is triggered once by
    // openInlinePanel()/refreshActiveInlinePanel() after the panel is rendered.
    // Triggering a refresh here as well would call the change-tracking endpoint twice
    // (init is emitted synchronously during openInlinePanel's detectChanges()).
    this.changeTrackingInitFn = initFn;
  }

  public reloadEmployeeAndContractsData() {
    this.getEmployeeData(true).pipe(tap(() => this.selectContract({id: this.contractId}))).subscribe();
  }

  private checkContractAlerts(): void {
    if (this.selectedContract?.id) {
      this.contractAlertsService.getAlerts(this.selectedContract.id).pipe(
        tap((alerts) => {
          this.hasContractAlerts = alerts && alerts.length > 0;
          this.cd.markForCheck();
        }),
        catchError(() => {
          this.hasContractAlerts = false;
          return of([]);
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
    }
  }

  public openInlinePanel(panel: string): void {
    this.activeInlinePanel = panel;
    this.isInlinePanelOpen = true;
    this.cd.detectChanges();
    this.updateInlinePanelViewportHeight();
    this.refreshActiveInlinePanel();
  }

  private updateInlinePanelViewportHeight(): void {
    setTimeout(() => {
      const containerTop = this.employeeDetailContentRef?.nativeElement?.getBoundingClientRect()?.top || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const bottomSpacing = 16;
      const computedHeight = Math.max(viewportHeight - containerTop - bottomSpacing, 320);
      this.inlinePanelHeightPx = `${computedHeight}px`;
      this.cd.markForCheck();
    });
  }

  private refreshActiveInlinePanel(): void {
    if (!this.selectedContract?.id) {
      return;
    }

    switch (this.activeInlinePanel) {
      case this.INLINE_PANEL.ATTACHMENTS:
        setTimeout(() => {
          if (this.attachmentsPanel) {
            this.attachmentsPanel.load(this.selectedContract.id, this.values, this.formDescriptor);
            this.attachmentsPanel.isReadOnly = this.readOnly || this.selectedContract.readOnly;
          }
        });
        break;

      case this.INLINE_PANEL.CHANGE_TRACKING:
        if (this.changeTrackingInitFn) {
          this.changeTrackingInitFn(this.selectedContract.id, this.getChangeTrackingType(), 'EMP');
        } else {
          setTimeout(() => {
            this.changeTrackingPanel?.load(this.selectedContract.id, this.getChangeTrackingType(), 'EMP');
          });
        }
        break;

      case this.INLINE_PANEL.EVENTS_LOG:
        setTimeout(() => {
          this.logEventPanel?.load(this.selectedContract.id, ItemType.CONTRACT,
            !this.selectedContractSdcStatus?.personMasterDataSyncOnSDC2Activated ? 'SDConnect' : !this.shouldDisplaySDC3() ? 'SDC3' : null);
        });
        break;

      case this.INLINE_PANEL.ALERTS:
        setTimeout(() => {
          this.contractAlertsPanel?.load(this.selectedContract.id);
        });
        break;

      case this.INLINE_PANEL.PERSON_MASTER_DATA_ADMIN:
        setTimeout(() => {
          this.personMasterDataAdminPanel?.load(this.selectedContract.id.toString(), this.selectedContractSdcStatus);
        });
        break;

      case this.INLINE_PANEL.PAYROLL_INPUT_ADMIN:
        setTimeout(() => {
          this.payrollInputAdminPanel?.load(this.selectedContract.id.toString());
        });
        break;

      case this.INLINE_PANEL.SDC_PERSON:
        setTimeout(() => {
          this.sdcPersonPanel?.load(this.selectedContract.id.toString());
        });
        break;

      case this.INLINE_PANEL.SDC_PAYROLL:
        setTimeout(() => {
          this.sdcPayrollPanel?.load(
            this.selectedContract.id.toString(),
            this.selectedContract.begindate,
            this.selectedContract.enddate
          );
        });
        break;

      case this.INLINE_PANEL.PAYSLIPS:
        setTimeout(() => {
          this.payslipsPanel?.load(this.selectedContract.id);
        });
        break;

      case this.INLINE_PANEL.TIME:
        this.isTimeReadOnly = this.readOnly
          || this.selectedContract.readOnly
          || !this.rolesService.hasRole('ABSENCE_PEOPLE_WRITE');
        break;

      default:
        break;
    }
  }

  private addDescriptorMetadatas(descriptor: FormDescriptor): void {
    descriptor.tabSheets.forEach((tabSheet: TabSheetDescriptor) => {
      tabSheet.groupZones.forEach((groupZone: GroupZoneDescriptor) => {
        this.fetchInheritedValues(descriptor, tabSheet, groupZone);

        if (!groupZone.metadatas) {
          groupZone.metadatas = {};
        }
        groupZone.metadatas.contractDates = {
          beginDate: this.selectedContract.begindate,
          endDate: this.selectedContract.enddate
        };

        if (groupZone.internalType === 'PEOPLE_TREE' || groupZone.groupZoneType === 'UNIQUE_MANDATORY') {
          if (!groupZone.metadatas) {
            groupZone.metadatas = {};
          }

          if (groupZone.internalType === 'PEOPLE_TREE') {
            groupZone.metadatas.employeeId = this.employee.id;
            groupZone.metadatas.onUpdate = this.peopleTreeEmitter;
          }

          groupZone.metadatas.dateRange = this.getContractsDateRange(this.contracts);
        }

        if (this.selectedContract) {
          groupZone.zones.forEach((zone: ZoneDescriptor) => {
            if (zone.type === 'FILE') {
              if (!zone.metadatas) {
                zone.metadatas = {};
              }
              zone.metadatas.contractId = this.selectedContract.id;
            }

            if (zone.type === 'BREAK_LINE') {
              zone.nbColumns = 4;
            }
          });
        }
      });
    });
  }

  private fetchInheritedValues(descriptor: FormDescriptor,
                               tabSheetDescriptor: TabSheetDescriptor,
                               groupZoneDescriptor: GroupZoneDescriptor): void {

    groupZoneDescriptor.zones.forEach((zone: ZoneDescriptor) => {
      if (zone.repInheritedValue) {

        const inheritedGroupZoneId: string = zone.repInheritedValue.split(':')[0];
        descriptor.tabSheets.forEach((inheritedTabSheet: TabSheetDescriptor) => {

          const foundGroupZone: GroupZoneDescriptor = inheritedTabSheet.groupZones
            .find((groupZone: GroupZoneDescriptor) => groupZone.groupZoneID === inheritedGroupZoneId);

          if (!foundGroupZone) {
            return;
          }

          const zonePath: string[] = [inheritedTabSheet.tabSheetID, inheritedGroupZoneId];
          const inheritedGroupZone = this.inheritedGroupZonesMap.get(inheritedGroupZoneId);

          if (inheritedGroupZone) {
            inheritedGroupZone.editGroupZonesMap.set(groupZoneDescriptor.groupZoneID, {
              groupZone: groupZoneDescriptor,
              path: [tabSheetDescriptor.tabSheetID, groupZoneDescriptor.groupZoneID]
            });
            return;
          }

          const editGroupZonesMap: Map<string, any> = new Map<string, any>();
          editGroupZonesMap.set(groupZoneDescriptor.groupZoneID, {
            groupZone: groupZoneDescriptor,
            path: [tabSheetDescriptor.tabSheetID, groupZoneDescriptor.groupZoneID]
          });

          this.inheritedGroupZonesMap.set(inheritedGroupZoneId, {
            path: zonePath,
            groupZone: foundGroupZone,
            editGroupZonesMap
          });
        });
      }
    });
  }

  private isFileZoneExist(descriptor: FormDescriptor): boolean {
    return descriptor.tabSheets.some((tabSheet: TabSheetDescriptor) => {
      return tabSheet.groupZones.some((groupZone: GroupZoneDescriptor) => {
        return groupZone.zones.some((zone: ZoneDescriptor) => zone.type === 'FILE');
      });
    });
  }

  private getContractsDateRange(contracts: Array<Data<EmpContractAttributes>>): any {
    let minimumDate: string = DATE_MAX;
    let maximumDate: string = DATE_MIN;
    contracts.forEach((contract: any) => {
      const beginDate: string = contract.attributes ? contract.attributes.begindate : contract.begindate;
      const endDate: string = contract.attributes ? contract.attributes.enddate : contract.enddate;
      if (moment(endDate).isAfter(maximumDate)) {
        maximumDate = endDate;
      }

      if (moment(beginDate).isBefore(minimumDate)) {
        minimumDate = beginDate;
      }
    });

    return {
      minimumDate,
      maximumDate
    };
  }

  public getDescriptorZoneNames(groupName: string): string[] {
    const isBankingDetailsGroupZone = (groupZone) => groupZone.internalType === 'BANKING_DETAILS';
    const groupNameSplit = groupName.split('_');
    const rawGroupName = groupNameSplit[0];
    const keyZoneValue = groupNameSplit[1] ? groupNameSplit[1] : null;
    const zonesNames = [];
    const groupZones = this.formDescriptor.tabSheets.flatMap(tabSheet => tabSheet.groupZones);
    for (const groupZone of groupZones) {
      if (isBankingDetailsGroupZone(groupZone) && rawGroupName === groupZone.groupZoneID) {
        groupZone.zones
          .filter(zone => DataTransformer.zoneTypeHasData(zone.type))
          .forEach(zone => zonesNames.push(zone.zoneID + '_' + keyZoneValue));
        break;
      } else if (!isBankingDetailsGroupZone(groupZone) && groupName === groupZone.groupZoneID) {
        groupZone.zones
          .filter(zone => DataTransformer.zoneTypeHasData(zone.type))
          .forEach(zone => zonesNames.push(zone.zoneID));
        break;
      }
    }
    return zonesNames;
  }

  private removeDeletedContracts(contractIdsDeleted: number[]): void {
    this.contracts = this.contracts.filter(contract => !contractIdsDeleted.includes(contract.id));
  }

  private handleRedirection(): void {
    let contractIdToRedirect: number;

    if (this.contracts.length) {
      contractIdToRedirect = this.contracts[0].id;
    }

    const uriFragment = this.isSkillModule ? 'employees/skills' : 'employees/files';

    if (!contractIdToRedirect) {
      this.router.navigate([uriFragment]);
    } else if (contractIdToRedirect !== this.contractId) {
      this.selectedContractDeleted = true;
      this.router.navigate([uriFragment, contractIdToRedirect]);
    }
  }

  private getTabByZoneName(zoneName: string): string {
    let tabSheet = this.zonesByTab.get(zoneName);
    if (!tabSheet && zoneName.includes('_')) {
      tabSheet = this.zonesByTab.get(zoneName.substring(0, zoneName.lastIndexOf('_')));
    }
    return tabSheet ?? '';
  }

  private getGroupZoneByZoneName(zoneName: string): string {
    if (!this.formDescriptor) {
      return null;
    }
    for (const tabSheet of this.formDescriptor.tabSheets) {
      for (const groupZone of tabSheet.groupZones) {
        if (groupZone.groupZoneID === zoneName) {
          return groupZone.groupZoneID;
        }
        const hasZone = groupZone.zones.some(zone => zone.zoneID === zoneName);
        if (hasZone) {
          return groupZone.groupZoneID;
        }
      }
    }
    // Try with underscore suffix stripped (for banking details etc.)
    if (zoneName.includes('_')) {
      return this.getGroupZoneByZoneName(zoneName.substring(0, zoneName.lastIndexOf('_')));
    }
    return null;
  }
}
